import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Flag, Trophy, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicStandings() {
  const { championshipId } = useParams();
  const [championship, setChampionship] = useState(null);
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [includeAttendance, setIncludeAttendance] = useState(false);

  const fetchStandings = async () => {
    try {
      const response = await fetch(
        `${API}/public/standings/${championshipId}?include_attendance=${includeAttendance}`
      );
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('Clasificación no encontrada o no pública');
        } else {
          setError('Error al cargar la clasificación');
        }
        return;
      }
      
      const data = await response.json();
      setChampionship(data.championship);
      setStandings(data.standings || []);
      setError(null);
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStandings();
  }, [championshipId, includeAttendance]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="bg-card/50 border-white/10 max-w-md">
          <CardContent className="py-12 text-center">
            <Trophy className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="font-heading text-xl uppercase mb-2">Error</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background noise-overlay" data-testid="public-standings-page">
      {/* Header */}
      <div 
        className="relative py-12 lg:py-16"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1718434144260-e19ab6474279?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black/80" />
        <div className="relative z-10 max-w-6xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Flag className="w-10 h-10 text-primary" />
            <h1 className="font-heading font-black text-3xl lg:text-4xl uppercase">
              {championship?.name}
            </h1>
          </div>
          {championship?.description && (
            <p className="text-zinc-400 max-w-xl mx-auto">
              {championship.description}
            </p>
          )}
          <p className="text-sm text-muted-foreground mt-4">
            Clasificación General AECAR
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Attendance Toggle */}
        <Card className="bg-card/50 border-white/10 mb-6">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Puntos de Asistencia</Label>
                <p className="text-xs text-muted-foreground">
                  +150 (4 carreras) | +100 (3 carreras) | +50 (2 carreras)
                </p>
              </div>
              <Switch
                checked={includeAttendance}
                onCheckedChange={setIncludeAttendance}
                data-testid="public-attendance-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Standings Table */}
        <Card className="bg-card/50 border-white/10">
          <CardHeader>
            <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
              <Trophy className="w-5 h-5 text-primary" />
              Clasificación
            </CardTitle>
          </CardHeader>
          <CardContent>
            {standings.length === 0 ? (
              <div className="text-center py-12">
                <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">
                  Aún no hay resultados publicados.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table className="standings-table">
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="w-12">Pos</TableHead>
                      <TableHead>Piloto</TableHead>
                      <TableHead className="text-center">C1</TableHead>
                      <TableHead className="text-center">C2</TableHead>
                      <TableHead className="text-center">C3</TableHead>
                      <TableHead className="text-center">C4</TableHead>
                      <TableHead className="text-center hidden md:table-cell">Total</TableHead>
                      <TableHead className="text-center hidden md:table-cell">Desc.</TableHead>
                      {includeAttendance && (
                        <TableHead className="text-center hidden lg:table-cell">Asist.</TableHead>
                      )}
                      <TableHead className="text-right">Final</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {standings.map((entry) => {
                      // Calculate which points are discarded (the lowest ones)
                      const totalRaces = championship?.total_races || 4;
                      const racesToCount = championship?.races_to_count || 3;
                      const numDiscards = totalRaces - racesToCount;
                      const pointsWithIndex = entry.race_points.map((pts, idx) => ({ idx, pts }));
                      const sortedByPoints = [...pointsWithIndex].sort((a, b) => a.pts - b.pts);
                      const discardedIndices = new Set(sortedByPoints.slice(0, numDiscards).map(x => x.idx));
                      
                      return (
                      <TableRow 
                        key={entry.license_number}
                        className={`border-white/5 ${
                          entry.position === 1 ? 'podium-1' :
                          entry.position === 2 ? 'podium-2' :
                          entry.position === 3 ? 'podium-3' : ''
                        }`}
                        data-testid={`public-standing-row-${entry.position}`}
                      >
                        <TableCell className="font-bold text-lg">
                          {entry.position === 1 && '🥇'}
                          {entry.position === 2 && '🥈'}
                          {entry.position === 3 && '🥉'}
                          {entry.position > 3 && `${entry.position}º`}
                        </TableCell>
                        <TableCell className="font-medium">
                          {entry.pilot_name}
                          {entry.pilot_found === false && (
                            <span className="block text-xs text-red-400 italic">
                              Piloto no encontrado
                            </span>
                          )}
                        </TableCell>
                        {entry.race_points.map((pts, idx) => (
                          <TableCell 
                            key={idx} 
                            className={`text-center font-mono text-sm ${
                              discardedIndices.has(idx)
                                ? 'line-through text-muted-foreground' 
                                : entry.race_results[idx] === null 
                                  ? 'text-muted-foreground' 
                                  : ''
                            }`}
                          >
                            {entry.race_results[idx] !== null 
                              ? `${pts} (${entry.race_results[idx]}º)` 
                              : '-'}
                          </TableCell>
                        ))}
                        <TableCell className="text-center hidden md:table-cell font-mono text-sm text-muted-foreground">
                          {entry.total_points}
                        </TableCell>
                        <TableCell className="text-center hidden md:table-cell font-mono text-sm text-muted-foreground line-through">
                          -{entry.discarded_points}
                        </TableCell>
                        {includeAttendance && (
                          <TableCell className="text-center hidden lg:table-cell font-mono text-sm text-secondary">
                            +{entry.attendance_bonus}
                          </TableCell>
                        )}
                        <TableCell className="text-right font-mono font-bold text-primary text-lg">
                          {entry.final_points}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-muted-foreground">
          <p>Sistema de puntuación AECAR</p>
          <p className="font-mono text-xs mt-1">
            Descarte automático del peor resultado | Desempate Art. 18.1
          </p>
        </div>
      </div>
    </div>
  );
}
